import { useCallback, useEffect, useState } from 'react'
import {
	downloadStudentApplicationDocument,
	fetchStudentApplicationDocuments,
} from 'api/applicationDocuments'

const formatRowTime = iso => {
	if (!iso) return ''
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return ''
	return d.toLocaleString(undefined, {
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
		day: '2-digit',
		month: '2-digit',
		year: '2-digit',
	})
}

const mapDocItem = row => ({
	id: String(row?.id || ''),
	title: String(row?.title || 'Document'),
	lastUpdated: formatRowTime(row?.lastUpdated),
	fileId: row?.fileId || null,
	itemType: 'document',
})

export const useStudentDocVaultTab = studentId => {
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState('')
	const [documentGroups, setDocumentGroups] = useState([])
	const [pdfPreview, setPdfPreview] = useState({
		open: false,
		url: '',
		title: '',
	})

	useEffect(() => {
		if (!studentId) return
		let alive = true
		const run = async () => {
			try {
				setIsLoading(true)
				setError('')
				const sid = String(studentId).trim()
				const docsRes = await fetchStudentApplicationDocuments(sid)

				if (!alive) return

				const groups = (docsRes?.groups || []).map(group => ({
					id: String(group?.id || ''),
					title: String(group?.title || 'Documents'),
					items: (group?.items || [])
						.filter(item => item?.counselorStatus === 'success' && item?.fileId)
						.map(mapDocItem),
				}))
				setDocumentGroups(groups)
			} catch (e) {
				if (!alive) return
				setError(e?.message || 'Failed to load Doc Vault')
				setDocumentGroups([])
			} finally {
				if (alive) setIsLoading(false)
			}
		}
		run()
		return () => {
			alive = false
		}
	}, [studentId])

	const openDocument = useCallback(
		async item => {
			const sid = String(studentId || '').trim()
			if (!sid || !item?.fileId) return
			try {
				const blob = await downloadStudentApplicationDocument(sid, item.fileId)
				const url = URL.createObjectURL(blob)
				setPdfPreview(prev => {
					if (prev.url && String(prev.url).startsWith('blob:')) {
						try {
							URL.revokeObjectURL(prev.url)
						} catch {
							// noop
						}
					}
					return { open: true, url, title: item.title || 'Document' }
				})
			} catch {
				// noop
			}
		},
		[studentId],
	)

	const closePreview = useCallback(() => {
		setPdfPreview(prev => {
			if (prev.url && String(prev.url).startsWith('blob:')) {
				try {
					URL.revokeObjectURL(prev.url)
				} catch {
					// noop
				}
			}
			return { open: false, url: '', title: '' }
		})
	}, [])

	return {
		isLoading,
		error,
		documentGroups,
		pdfPreview,
		openDocument,
		closePreview,
	}
}

export default useStudentDocVaultTab
